import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  useInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { meQuery, requireAuth } from "@/lib/auth-guard";
import type { UserResponse } from "@/api/auth";
import {
  focalPoint,
  groupDisplayName,
  listCollectionItems,
  listGroups,
  tileUrl,
  type CollectionFamily,
  type MediaCollectionGroup,
} from "@/api/memories";
import { FocalImage } from "@/components/FocalImage";
import { PhotoTile } from "@/components/memories";
import { Lightbox } from "@/components/lightbox";
import { TailSentinel } from "@/components/TailSentinel";
import { StoryCloud } from "@/components/storyCloud";
import { LoadingBlock, QuietButton } from "@/components/ui";
import { countLabel } from "@/lib/format";

/**
 * Memories drill — the iOS Memilies collection browse. Level 2 is
 * the grouped grid (people as face-centered circles, trips/days as
 * scene covers); picking a group swaps to the flat 3-column item
 * grid via the ?key= search param, so back/forward and deep links
 * work. Story Themes has no backend surface yet — it renders the
 * iOS gentle empty state.
 */

const CATEGORY_META: Record<
  string,
  { title: string; family: CollectionFamily | null }
> = {
  people: { title: "People", family: "people" },
  trips: { title: "Trips", family: "trips" },
  "time-travel": { title: "Time Travel", family: "recent_days" },
  "story-themes": { title: "Story Themes", family: null },
};

const MemoriesCategoryPage = () => {
  const { data: user } = useSuspenseQuery<UserResponse>(meQuery);
  const { category } = Route.useParams();
  const { key, title } = Route.useSearch();
  const meta = CATEGORY_META[category];

  return (
    <AppShell user={user}>
      <div className="mx-auto max-w-4xl">
        {!meta ? (
          <NotAPlace />
        ) : key ? (
          <ItemsView
            category={category}
            categoryTitle={meta.title}
            collectionKey={key}
            groupTitle={title ?? ""}
          />
        ) : meta.family ? (
          <GroupsView category={category} meta={meta} />
        ) : (
          <StoryThemesEmpty />
        )}
      </div>
    </AppShell>
  );
};

const Crumb = ({
  parts,
}: {
  parts: Array<{ label: string; to?: string; search?: object }>;
}) => (
  <nav aria-label="Breadcrumb" className="mb-1 flex flex-wrap items-center gap-1.5">
    <Link
      to="/home"
      className="font-mono text-[12px] uppercase tracking-[0.18em] text-taupe-deep hover:text-ink"
    >
      Your memories
    </Link>
    {parts.map((p) => (
      <span key={p.label} className="flex items-center gap-1.5">
        <span aria-hidden className="text-taupe-deep">
          ›
        </span>
        {p.to ? (
          <Link
            to={p.to}
            className="font-mono text-[12px] uppercase tracking-[0.18em] text-taupe-deep hover:text-ink"
          >
            {p.label}
          </Link>
        ) : (
          <span className="font-mono text-[12px] uppercase tracking-[0.18em] text-ink">
            {p.label}
          </span>
        )}
      </span>
    ))}
  </nav>
);

// ── Level 2: groups ─────────────────────────────────────────────────

const GroupsView = ({
  category,
  meta,
}: {
  category: string;
  meta: { title: string; family: CollectionFamily | null };
}) => {
  const groups = useInfiniteQuery({
    queryKey: ["media-groups", meta.family] as const,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      listGroups(meta.family!, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 60_000,
  });
  const rows = useMemo(
    () => groups.data?.pages.flatMap((p) => p.items) ?? [],
    [groups.data],
  );

  return (
    <div>
      <Crumb parts={[{ label: meta.title }]} />
      <h1
        className="font-serif text-[28px] leading-[34px] text-ink"
        style={{ fontWeight: 600 }}
      >
        {meta.title}
      </h1>

      {groups.isPending ? (
        <LoadingBlock label={`Gathering your ${meta.title.toLowerCase()}…`} />
      ) : groups.isError ? (
        <div className="py-10 text-center">
          <p className="font-serif text-[15px] italic text-ink-soft">
            Couldn't load this collection.
          </p>
          <div className="mt-3">
            <QuietButton onClick={() => groups.refetch()}>
              Try again
            </QuietButton>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <p className="py-10 text-center font-serif text-[15px] italic text-ink-soft">
          Nothing here yet.
        </p>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {rows.map((group) => (
            <GroupTile key={group.group_key} category={category} group={group} />
          ))}
          {groups.hasNextPage && (
            <TailSentinel
              onVisible={() => {
                if (!groups.isFetchingNextPage) void groups.fetchNextPage();
              }}
              fetching={groups.isFetchingNextPage}
            />
          )}
        </div>
      )}
    </div>
  );
};

const GroupTile = ({
  category,
  group,
}: {
  category: string;
  group: MediaCollectionGroup;
}) => {
  const isPeople = group.type === "face_cluster" || group.type === "identity";
  const url = tileUrl(group.cover);
  // People avatars center on the group's own cover_face (the face
  // that defines the cluster), not the largest face in the photo.
  const focal = group.cover_face?.box
    ? {
        x: group.cover_face.box.x + group.cover_face.box.w / 2,
        y: group.cover_face.box.y + group.cover_face.box.h / 2,
      }
    : focalPoint(group.cover);

  return (
    <Link
      to="/memories/$category"
      params={{ category }}
      search={{ key: group.collection_key, title: groupDisplayName(group) }}
      className="group flex flex-col items-center gap-2 rounded-2xl p-3 transition-colors hover:bg-white"
    >
      {isPeople ? (
        <span
          className="block h-[104px] w-[104px] overflow-hidden rounded-full bg-sand/60"
          aria-hidden
        >
          {url && (
            <FocalImage
              src={url}
              focal={focal}
              width={group.cover?.width}
              height={group.cover?.height}
              className="h-full w-full"
            />
          )}
        </span>
      ) : (
        <span
          className="block aspect-square w-full overflow-hidden rounded-2xl bg-sand/60"
          aria-hidden
          style={{
            backgroundColor: group.cover?.dominant_color ?? undefined,
          }}
        >
          {url && (
            <FocalImage
              src={url}
              focal={focal}
              width={group.cover?.width}
              height={group.cover?.height}
              className="h-full w-full"
            />
          )}
        </span>
      )}
      <span className="text-center">
        {/* Identities show their hydrated name; unbound face clusters
            show none — the stored title is a projection placeholder
            ("Person"/"Unknown Person"), never rendered (iOS parity). */}
        {groupDisplayName(group) && (
          <span
            className="block font-serif text-[14px] leading-[18px] text-ink"
            style={{ fontWeight: 600 }}
          >
            {groupDisplayName(group)}
          </span>
        )}
        <span className="block font-sans text-[12px] text-ink-soft">
          {countLabel(group.count, "photo")}
        </span>
      </span>
    </Link>
  );
};

// ── Level 3: flat item grid ─────────────────────────────────────────

const ItemsView = ({
  category,
  categoryTitle,
  collectionKey,
  groupTitle,
}: {
  category: string;
  categoryTitle: string;
  collectionKey: string;
  groupTitle: string;
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const items = useInfiniteQuery({
    queryKey: ["media-collection-items", collectionKey] as const,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      listCollectionItems(collectionKey, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 60_000,
  });
  const list = useMemo(
    () => items.data?.pages.flatMap((p) => p.items) ?? [],
    [items.data],
  );

  return (
    <div>
      <Crumb
        parts={[
          { label: categoryTitle, to: `/memories/${category}` },
          { label: groupTitle || "Photos" },
        ]}
      />
      <h1
        className="font-serif text-[28px] leading-[34px] text-ink"
        style={{ fontWeight: 600 }}
      >
        {groupTitle || categoryTitle}
      </h1>

      {items.isPending ? (
        <LoadingBlock label="Gathering these memories…" />
      ) : items.isError ? (
        <div className="py-10 text-center">
          <p className="font-serif text-[15px] italic text-ink-soft">
            Couldn't load this collection.
          </p>
          <div className="mt-3">
            <QuietButton onClick={() => items.refetch()}>
              Try again
            </QuietButton>
          </div>
        </div>
      ) : list.length === 0 ? (
        <p className="py-10 text-center font-serif text-[15px] italic text-ink-soft">
          Nothing here yet.
        </p>
      ) : (
        <div className="mt-6 grid grid-cols-3 gap-0.5 md:grid-cols-6">
          {list.map((item, i) => (
            <PhotoTile
              key={item.item_id}
              item={item}
              onOpen={() => setOpenIndex(i)}
            />
          ))}
          {items.hasNextPage && (
            <TailSentinel
              onVisible={() => {
                if (!items.isFetchingNextPage) void items.fetchNextPage();
              }}
              fetching={items.isFetchingNextPage}
            />
          )}
        </div>
      )}

      {openIndex !== null && (
        <Lightbox
          items={list}
          index={openIndex}
          onClose={() => setOpenIndex(null)}
          onNavigate={setOpenIndex}
        />
      )}
    </div>
  );
};

// ── Story Themes (no backend surface yet) + unknown category ───────

const StoryThemesEmpty = () => (
  <div>
    <Crumb parts={[{ label: "Story Themes" }]} />
    <h1
      className="font-serif text-[28px] leading-[34px] text-ink"
      style={{ fontWeight: 600 }}
    >
      Story Themes
    </h1>
    <p className="mt-1 font-serif text-[14px] italic text-ink-soft">
      Browse by values, people &amp; places
    </p>
    <div className="mt-6">
      <StoryCloud />
    </div>
  </div>
);

const NotAPlace = () => (
  <div className="py-10 text-center">
    <p className="font-serif text-[15px] italic text-ink-soft">
      Nothing here yet.
    </p>
    <div className="mt-4">
      <Link
        to="/home"
        className="font-sans text-[14px] font-medium text-taupe-deep hover:text-ink"
      >
        Back to your memories
      </Link>
    </div>
  </div>
);

export const Route = createFileRoute("/memories/$category")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { key?: string; title?: string } => ({
    key: typeof search.key === "string" ? search.key : undefined,
    title: typeof search.title === "string" ? search.title : undefined,
  }),
  loader: ({ context }) => requireAuth(context.queryClient),
  component: MemoriesCategoryPage,
});
